import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LayoutWide from "../layouts/LayoutWide.jsx";
import DeviceSelect from "../components/DeviceSelect.jsx";
import SensorAssignmentPicker from "../components/SensorAssignmentPicker.jsx";
import UnitConverter from "../components/UnitConverter.jsx";
import { useCreateInventoryItem, useInventoryQuery, useStrainCatalogQuery, useReferralLinksQuery } from "../hooks/useInventoryQuery.js";
import { apiFetch } from "../utility/apiFetch.js";
import { UNIT_GROUPS, toBaseValue, storageHint } from "../utility/unitConversions.js";
import { BIO_CATEGORIES, BOM_CATEGORIES, PARENT_CATEGORIES, CATEGORY_LABELS, CATEGORY_ORDER } from "../utility/inventoryConstants.js";
import usePlan from "../hooks/usePlan.js";
import Button from "../components/ui/Button.jsx";
import Input from "../components/ui/Input.jsx";
import Select from "../components/ui/Select.jsx";
import Modal from "../components/ui/Modal.jsx";

const API = import.meta.env.VITE_API_BASE_URL;

// Sensors for the currently-picked device, sourced from the same "dashboard" query
// DeviceSelect/InventoryDetail's device panel already use — no new endpoint needed.
// Mirrors the identical helper in GrowNew.jsx.
function useDeviceSensors(deviceId) {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 60_000,
    enabled: !!deviceId,
    select: (data) => data.devices?.[deviceId]?.sensors ?? [],
  });
}

const PLAN_LIMITS = { hobby: 5, pro: 50, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <Modal open onClose={onClose} title="Inventory limit reached">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        You&apos;ve reached your Hobby plan limit ({current}/{limit} items).<br />
        To upgrade to Pro, email us at{" "}
        <a href="mailto:hello@fungistics.com" className="text-sky-700 dark:text-sky-400 underline">hello@fungistics.com</a>.
      </p>
      <Button onClick={onClose} className="w-full">Got it</Button>
    </Modal>
  );
}

const CATEGORIES = CATEGORY_ORDER.map((v) => ({ value: v, label: CATEGORY_LABELS[v] }));

const CONTAINER_TYPES = [
  { value: "syringe", label: "Syringe" },
  { value: "mason_jar", label: "Mason Jar" },
  { value: "agar_plate", label: "Agar Plate" },
  { value: "grain_jar", label: "Grain Jar" },
  { value: "bag", label: "Bag" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "growing", label: "Growing" },
  { value: "ready", label: "Ready" },
  { value: "depleted", label: "Depleted" },
  { value: "contaminated", label: "Contaminated" },
];

function defaultStatus(cat) {
  switch (cat) {
    case "materials":
    case "supplies":
      return "ready";
    case "products":
      return "depleted";
    default:
      return "growing";
  }
}

export default function InventoryNew() {
  const navigate = useNavigate();
  const createItem = useCreateInventoryItem();
  const { data: catalog = [] } = useStrainCatalogQuery();
  const { plan, planLoading } = usePlan();
  const { data: inventoryItems = [] } = useInventoryQuery();
  const { data: referralLinks = [] } = useReferralLinksQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const itemCount = inventoryItems?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 5;
  const atLimit = !planLoading && itemCount >= limit;

  const [category, setCategory] = useState("cultures");
  const [name, setName] = useState("");
  const [catalogStrain, setCatalogStrain] = useState("");
  const [species, setSpecies] = useState("");
  const [generation, setGeneration] = useState("");
  const [containerType, setContainerType] = useState("");
  const [quantity, setQuantity] = useState("");
  const [displayUnit, setDisplayUnit] = useState("syringes");
  const [status, setStatus] = useState(defaultStatus("cultures"));
  const [startedAt, setStartedAt] = useState("");
  const [inoculatedAt, setInoculatedAt] = useState("");
  const [readyAt, setReadyAt] = useState("");
  const [deviceId, setDeviceId] = useState("");
  // New items default to an explicit opt-in ("custom", nothing picked) rather than
  // silently monitoring the whole device — matches the grow feature's default
  // behavior. See infra/.planning/proposals/sensor-assignments.md.
  const [sensorMode, setSensorMode] = useState("custom");
  const [sensorIds, setSensorIds] = useState([]);
  const [sensorWarning, setSensorWarning] = useState(null);
  const [createdItemId, setCreatedItemId] = useState(null);
  const { data: deviceSensors = [] } = useDeviceSensors(deviceId ? Number(deviceId) : null);
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [referralLinkId, setReferralLinkId] = useState("");
  const [defaultDeductQty, setDefaultDeductQty] = useState("");
  const [notes, setNotes] = useState("");
  const [showSource, setShowSource] = useState(false);
  const [parentItemId, setParentItemId] = useState("");
  const [costPerUnit, setCostPerUnit] = useState("");
  const [costMode, setCostMode] = useState("direct"); // "direct" | "calc"
  const [costQty, setCostQty] = useState("");
  const [costTotal, setCostTotal] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState(null);

  const isBio = BIO_CATEGORIES.has(category);
  const isBom = BOM_CATEGORIES.has(category);
  const showParent = PARENT_CATEGORIES.has(category);
  const showExpiry = !BOM_CATEGORIES.has(category);

  // Eligible parents: bio items that are not depleted or contaminated
  const parentOptions = inventoryItems.filter(
    (i) => PARENT_CATEGORIES.has(i.category) && i.status !== "depleted" && i.status !== "contaminated"
  );

  function handleCategoryChange(cat) {
    setCategory(cat);
    setStatus(defaultStatus(cat));
    if (!PARENT_CATEGORIES.has(cat)) setParentItemId("");
    if (BOM_CATEGORIES.has(cat)) setExpiresAt("");
  }

  function handleCatalogSelection(commonName) {
    setCatalogStrain(commonName);
    const entry = catalog.find((e) => e.common_name === commonName);
    if (entry) setSpecies(entry.species);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSensorWarning(null);

    const hint = storageHint(displayUnit);
    const dbUnit = hint
      ? UNIT_GROUPS.flatMap((g) => g.units).find((u) => u.display === displayUnit)?.db
      : displayUnit;

    const body = {
      category,
      name: name.trim(),
      unit: dbUnit,
      status,
      quantity: toBaseValue(parseFloat(quantity) || 0, displayUnit),
    };
    if (catalogStrain) body.catalog_strain = catalogStrain;
    if (species.trim()) body.species = species.trim();
    if (generation.trim()) body.generation = generation.trim();
    if (containerType) body.container_type = containerType;
    if (startedAt) body.started_at = new Date(startedAt).toISOString();
    if (inoculatedAt) body.inoculated_at = new Date(inoculatedAt).toISOString();
    if (readyAt) body.ready_at = new Date(readyAt).toISOString();
    if (deviceId) body.device_id = Number(deviceId);
    if (lowStockThreshold) body.low_stock_threshold = toBaseValue(parseFloat(lowStockThreshold), displayUnit);
    if (sourceName.trim()) body.source_name = sourceName.trim();
    if (sourceUrl.trim()) body.source_url = sourceUrl.trim();
    if (referralLinkId) body.referral_link_id = Number(referralLinkId);
    if (defaultDeductQty) body.default_deduct_qty = parseFloat(defaultDeductQty);
    if (notes.trim()) body.notes = notes.trim();
    if (parentItemId) body.parent_item_id = Number(parentItemId);
    const resolvedCost = costMode === "calc"
      ? (parseFloat(costQty) > 0 ? parseFloat(costTotal) / parseFloat(costQty) : null)
      : (costPerUnit ? parseFloat(costPerUnit) : null);
    if (resolvedCost != null && !isNaN(resolvedCost)) body.cost_per_unit = resolvedCost;
    if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();

    let item;
    try {
      item = await createItem.mutateAsync(body);
    } catch (err) {
      setError(err.message ?? "Failed to create item");
      return;
    }

    // Two-step create-then-assign: sensor_ids can't be submitted in the same POST
    // since the sensor-assignment endpoint is keyed by the item's id, which doesn't
    // exist until step 1 completes. Always sent, even at the untouched
    // custom/empty default — there is no "skip this step" case. sensor_mode is
    // deliberately NOT included in `body` above — item creation must never set it,
    // only this PUT is allowed to, so a failure here reliably means the item is
    // still at its DB default (device_wide).
    try {
      await apiFetch(`inventory/${item.id}/sensors`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sensor_mode: sensorMode, sensor_ids: sensorMode === "custom" ? sensorIds : [] }),
      });
      navigate(`/inventory/${item.id}`);
    } catch (err) {
      // Item creation itself succeeded — this is not a blocking error, but the
      // user's actual sensor choice was never recorded and the item is silently
      // monitoring the whole device instead. Per the "no silent fallbacks" design
      // decision, this must be a visible, non-dismissible-feeling notice, not a
      // toast — so we stay on this page instead of navigating away.
      setCreatedItemId(item.id);
      setSensorWarning(
        `Item created, but your sensor settings failed to save (${err.message ?? "unknown error"}). ` +
        `This item is currently monitoring the whole device by default — go to the item's detail page now to fix your sensor selection.`
      );
    }
  }

  const hint = storageHint(displayUnit);

  const inputCls = "w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500";
  const labelCls = "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1";

  return (
    <LayoutWide title="New Inventory Item">
      {showUpgrade && <UpgradeModal current={itemCount} limit={limit} onClose={() => setShowUpgrade(false)} />}
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/inventory")}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Back"
          >
            ←
          </button>
          <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">New Inventory Item</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className={labelCls}>Category <span className="text-red-500">*</span></label>
            <Select value={category} onChange={(e) => handleCategoryChange(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className={labelCls}>Name <span className="text-red-500">*</span></label>
            <Input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PE6 Liquid Culture"
            />
          </div>

          {isBio && (
            <div>
              <label className={labelCls}>Strain</label>
              <Input
                type="text"
                list="catalog-list"
                value={catalogStrain}
                onChange={(e) => handleCatalogSelection(e.target.value)}
                placeholder="Start typing a strain name…"
              />
              <datalist id="catalog-list">
                {catalog.map((e) => (
                  <option key={e.id} value={e.common_name} />
                ))}
              </datalist>
              {catalogStrain.trim() && !catalog.some((e) => e.common_name === catalogStrain) && (
                <p className="text-xs text-sky-700 dark:text-sky-400 mt-1">
                  "{catalogStrain.trim()}" isn't in the catalog yet — it will be saved as a new strain.
                </p>
              )}
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Species</label>
              <Input
                type="text"
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                placeholder="e.g. Psilocybe cubensis"
              />
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Generation</label>
              <Input
                type="text"
                value={generation}
                onChange={(e) => setGeneration(e.target.value)}
                placeholder="e.g. LC Gen 3, Agar Sub-culture 2"
              />
            </div>
          )}

          {isBio && (
            <div>
              <label className={labelCls}>Container type</label>
              <Select value={containerType} onChange={(e) => setContainerType(e.target.value)}>
                <option value="">— none —</option>
                {CONTAINER_TYPES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Select>
            </div>
          )}

          {showParent && (
            <div>
              <label className={labelCls}>Spawned from</label>
              <Select value={parentItemId} onChange={(e) => setParentItemId(e.target.value)}>
                <option value="">— none —</option>
                {parentOptions.map((i) => (
                  <option key={i.id} value={i.id}>{i.name} — {CATEGORY_LABELS[i.category] ?? i.category}</option>
                ))}
              </Select>
            </div>
          )}

          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Quantity <span className="text-red-500">*</span></label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  required
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={labelCls}>Unit <span className="text-red-500">*</span></label>
                <Select value={displayUnit} onChange={(e) => setDisplayUnit(e.target.value)}>
                  {UNIT_GROUPS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.units.map((u) => (
                        <option key={u.display} value={u.display}>{u.display}</option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
                {hint && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{hint}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Status</label>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </div>

          {isBio && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Started at</label>
                <Input type="date" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Inoculated at</label>
                <Input type="date" value={inoculatedAt} onChange={(e) => setInoculatedAt(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Ready at</label>
                <Input type="date" value={readyAt} onChange={(e) => setReadyAt(e.target.value)} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls + " mb-0"}>Cost per {displayUnit}</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setCostMode("direct")}
                  className={`px-2 py-0.5 rounded ${costMode === "direct" ? "bg-sky-100 dark:bg-sky-950 text-sky-700 dark:text-sky-400 font-medium" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}>
                  Direct
                </button>
                <button type="button" onClick={() => setCostMode("calc")}
                  className={`px-2 py-0.5 rounded ${costMode === "calc" ? "bg-sky-100 dark:bg-sky-950 text-sky-700 dark:text-sky-400 font-medium" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}>
                  Qty ÷ Total
                </button>
              </div>
            </div>
            {costMode === "direct" ? (
              <Input
                type="number"
                min="0"
                step="any"
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.target.value)}
                placeholder="Optional"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={costQty}
                  onChange={(e) => setCostQty(e.target.value)}
                  placeholder={`Qty (${displayUnit})`}
                />
                <span className="text-gray-400 dark:text-gray-500 text-sm shrink-0">÷</span>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={costTotal}
                  onChange={(e) => setCostTotal(e.target.value)}
                  placeholder="Total cost $"
                />
                <span className="text-gray-400 dark:text-gray-500 text-sm shrink-0">=</span>
                <span className="text-sm font-medium text-sky-700 dark:text-sky-400 whitespace-nowrap tabular-nums">
                  {costQty && costTotal && parseFloat(costQty) > 0
                    ? `$${(parseFloat(costTotal) / parseFloat(costQty)).toFixed(4)}`
                    : "—"}
                </span>
              </div>
            )}
          </div>

          {showExpiry && (
            <div>
              <label className={labelCls}>Expires</label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          )}

          {isBom && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              BOM ingredients can be added after the item is created.
            </div>
          )}

          <div>
            <label className={labelCls}>Storage device</label>
            <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
          </div>

          {deviceId && (
            <div>
              <label className={labelCls}>Sensors to track</label>
              <SensorAssignmentPicker
                sensors={deviceSensors}
                sensorMode={sensorMode}
                sensorIds={sensorIds}
                onChange={({ sensor_mode, sensor_ids }) => { setSensorMode(sensor_mode); setSensorIds(sensor_ids); }}
              />
              {sensorMode === "custom" && sensorIds.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  You haven&apos;t selected any sensors — this item won&apos;t show live readings until you do.
                </p>
              )}
            </div>
          )}

          {sensorWarning && (
            <div className="text-sm text-amber-800 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              {sensorWarning}{" "}
              <Link to={`/inventory/${createdItemId}`} className="underline font-medium">
                Go to item →
              </Link>
            </div>
          )}

          <div>
            <label className={labelCls}>Low stock threshold</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="any"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value)}
                placeholder="Alert when quantity falls to or below…"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{displayUnit}</span>
            </div>
          </div>

          <div>
            <label className={labelCls}>Default deduct quantity</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="any"
                value={defaultDeductQty}
                onChange={(e) => setDefaultDeductQty(e.target.value)}
                placeholder="e.g. 1"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">{displayUnit}</span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Pre-fills the deduct field when starting a grow with this item</p>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowSource((v) => !v)}
              className="text-sm text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 font-medium"
            >
              {showSource ? "▾" : "▸"} Source / Reorder info
            </button>
            {showSource && (
              <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700">
                {referralLinks.length > 0 && (
                  <div>
                    <label className={labelCls}>Reorder link</label>
                    <Select value={referralLinkId} onChange={(e) => setReferralLinkId(e.target.value)}>
                      <option value="">— none —</option>
                      {referralLinks.map((l) => (
                        <option key={l.id} value={l.id}>{l.name}{l.supplier ? ` (${l.supplier})` : ""}</option>
                      ))}
                    </Select>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Source name</label>
                  <Input type="text" value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="e.g. Mush Genetics" />
                </div>
                <div>
                  <label className={labelCls}>Source URL</label>
                  <Input type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://…" />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={inputCls}
            />
          </div>

          <UnitConverter />

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {atLimit && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-400">
              You&apos;ve reached your Hobby plan limit ({itemCount}/{limit} items).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createItem.isPending || !name.trim())) || planLoading}
              className="flex-1"
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createItem.isPending ? "Saving…" : "Create item"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/inventory")}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
